import { Injectable, OnDestroy, inject } from '@angular/core';
import { ITCResultResponse, TCErrorResponse, ITcResponse } from '../../common/models/ITcResponse';
import { HttpService } from '@tc/http';
import { ApiCheckSuspension } from '../../common/contracts/account/ApiCheckSuspension';
import { ApiCreateAccount } from '../../common/contracts/account/ApiCreateAccount';
import { ApiGetAccount } from '../../common/contracts/account/ApiGetAccount';
import { ApiUpdateAccount } from '../../common/contracts/account/ApiUpdateAccount';
import { ApiSetAlias } from '../../common/contracts/account/ApiSetAlias';
import { ApiGetGlobalNotifications } from '../../common/contracts/account/ApiGetGlobalNotifications';
import { AuthenticationService } from '@tc/security-oauth-legacy';
import { BehaviorSubject, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { catchError, first, map, takeUntil, tap } from 'rxjs/operators';
import { DropCatchWebApi } from '../../../environments/endpoints';
import { AccountStatus } from './models/AccountStatus';
import { AccountVerificationType } from './models/AccountVerificationType';
import { UserAccount } from './models/UserAccount';
import { AccountLiveUpdateHub } from './account-live-update.hub';
import { IUserAccountResponse } from './models/IUserAccountResponse';
import { UpdateUserAccount } from './models/UpdateUserAccount';
import { IUpdateAccountResponse } from './models/IUpdateAccountResponse';
import { ICreateAccountResponse } from './models/ICreateAccountResponse';
import { INotification } from '../../common/models/INotification';

@Injectable({ providedIn: 'root' })
export class AccountService implements OnDestroy {
  private http = inject(HttpService);
  private authService = inject(AuthenticationService);
  private accountLiveUpdateHub = inject(AccountLiveUpdateHub);

  private initialAccountLoadComplete = false;

  private readonly destroyed$ = new Subject<boolean>();

  private readonly requiresPhotoIdSubject = new ReplaySubject<boolean>(1);
  readonly requiresPhotoId$ = this.requiresPhotoIdSubject.asObservable();

  private readonly accountSubject = new ReplaySubject<UserAccount>(1);
  readonly account$ = this.accountSubject.asObservable();

  readonly accountDetails$ = new ReplaySubject<UserAccount>(1);
  readonly isCreating$ = new BehaviorSubject<boolean>(false);
  readonly isUpdating$ = new BehaviorSubject<boolean>(false);
  readonly isLoading$ = new BehaviorSubject<boolean>(true);
  readonly isLoaded$ = new BehaviorSubject<boolean>(false);
  readonly auctionUnverified$ = new ReplaySubject<boolean>(1);
  readonly isVerificationPending$ = new ReplaySubject<boolean>(1);
  readonly isAccountSuspended$ = new ReplaySubject<boolean>(1);
  readonly requiresVerificationData$ = new ReplaySubject<boolean>(1);
  readonly hasCreateError$ = new ReplaySubject<boolean>(1);

  constructor() {
    this.initialize();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
  }

  private initialize(): void {
    this.authService.isAuthenticated$.pipe(takeUntil(this.destroyed$)).subscribe((isAuthed) => {
      if (!this.initialAccountLoadComplete && isAuthed) {
        this.retrieveUserAccount();
      } else if (!isAuthed) {
        this.accountSubject.next(null);
        this.isLoaded$.next(false);
        this.isLoading$.next(false);
      }
    });

    this.accountLiveUpdateHub.hubUpdated$.pipe(takeUntil(this.destroyed$)).subscribe((isRefreshed) => {
      if (isRefreshed) {
        this.retrieveUserAccount();
      }
    });

    this.account$.pipe(takeUntil(this.destroyed$)).subscribe((account) => {
      if (account == null) {
        this.requiresPhotoIdSubject.next(false);
      } else {
        this.requiresPhotoIdSubject.next(
          !account.pendingVerifications.some((v) => v === AccountVerificationType['PhotoId']) &&
            !account.completedVerifications.some((v) => v === AccountVerificationType['PhotoId'])
        );
      }
    });
  }

  private retrieveUserAccount(): void {
    this.isLoading$.next(true);
    this.retrieveAccount().subscribe((response) => {
      if (response == null) {
        this.authService.logout();
        this.isLoading$.next(false);
        this.accountSubject.next(null);
      } else {
        this.handleRetrieveAccountResponse(response);
        this.isLoading$.next(false);
        this.isLoaded$.next(true);
        this.initialAccountLoadComplete = true;
      }
    });
  }

  private handleRetrieveAccountResponse(result: IUserAccountResponse): void {
    if (result == null) {
      this.authService.logout();
      return;
    }
    const user = this.buildUserFromResponse(result);
    this.accountSubject.next(user);
    this.setRequiresVerificationData(user);
    this.updateAccountSuspended();
    this.auctionUnverified(user);
    this.updateVerificationPending(user);
  }

  private buildUserFromResponse(result: IUserAccountResponse): UserAccount {
    const pendingVerifications: string[] =
      result.pendingVerifications == null || result.pendingVerifications.trim() === ''
        ? []
        : result.pendingVerifications.split(',').map((v) => v.trim());

    const completedVerifications: string[] =
      result.completedVerifications == null || result.completedVerifications.trim() === ''
        ? []
        : result.completedVerifications.split(',').map((v) => v.trim());

    const user: UserAccount = Object.assign({}, result, <UserAccount>{
      accountStatus: AccountStatus[result.accountStatus],
      pendingVerifications: pendingVerifications.map((v) => AccountVerificationType[v]),
      completedVerifications: completedVerifications.map((v) => AccountVerificationType[v]),
    });

    return user;
  }

  private retrieveAccount(): Observable<IUserAccountResponse> {
    return this.http.send<ApiGetAccount, ITcResponse<IUserAccountResponse>>(new ApiGetAccount()).pipe(
      map((response) => response.result),
      catchError(() => of(null))
    );
  }

  private setRequiresVerificationData(user: UserAccount): void {
    if (user == null) {
      this.requiresVerificationData$.next(false);
      return;
    }
    const missingCredit =
      user.completedVerifications == null ||
      (user.completedVerifications != null && !user.completedVerifications.includes(AccountVerificationType.CreditCard));
    // const missingPhoto =
    //   (user.completedVerifications == null && user.pendingVerifications == null) ||
    //   (user.completedVerifications != null &&
    //     !user.completedVerifications.includes(Verifications.PhotoId) &&
    //     user.pendingVerifications != null &&
    //     !user.pendingVerifications.includes(Verifications.PhotoId));
    const requiresData = missingCredit;
    this.requiresVerificationData$.next(requiresData);
  }

  private updateAccountSuspended(): void {
    this.http
      .send<ApiCheckSuspension, ITcResponse<boolean>>(new ApiCheckSuspension())
      .pipe(
        tap((response) => this.isAccountSuspended$.next(response.result)),
        catchError(() => of())
      )
      .subscribe();
  }

  private updateVerificationPending(user: UserAccount): void {
    const isVerificationPending =
      user != null && user.pendingVerifications != null && !user.pendingVerifications.includes(AccountVerificationType.None);
    this.isVerificationPending$.next(isVerificationPending);
  }

  private auctionUnverified(user: UserAccount): void {
    const auctionUnverified =
      user &&
      user.pendingVerifications &&
      user.completedVerifications &&
      user.pendingVerifications.includes(AccountVerificationType.None) &&
      user.completedVerifications.includes(AccountVerificationType.CreditCard);
    this.auctionUnverified$.next(auctionUnverified);
  }

  updateAccount(account: UpdateUserAccount): Observable<boolean> {
    this.isUpdating$.next(true);
    const apiContract = new ApiUpdateAccount(account);
    return this.http.send<ApiUpdateAccount, ITcResponse<IUpdateAccountResponse>>(apiContract).pipe(
      tap(() => {
        this.isUpdating$.next(false);
        this.retrieveUserAccount();
      }),
      map(() => true),
      catchError(() => of(false))
    );
  }

  setAlias(alias: string): Observable<boolean> {
    this.isUpdating$.next(true);
    return this.http.send<ApiSetAlias, ITcResponse<boolean>>(new ApiSetAlias(alias)).pipe(
      tap(() => {
        this.isUpdating$.next(false);
        this.retrieveUserAccount();
      }),
      map(() => true),
      catchError(() => of(false))
    );
  }

  updateCompletedVerifications(verification: AccountVerificationType): void {
    this.account$.pipe(first()).subscribe((account) => {
      if (account != null && !account.completedVerifications.includes(verification)) {
        account.completedVerifications.push(verification);
        this.setRequiresVerificationData(account);
        this.updateVerificationPending(account);
        this.accountSubject.next(account);
      }
    });
  }

  createAccount(account: UserAccount): Observable<boolean> {
    this.isCreating$.next(true);
    const { endpoint } = DropCatchWebApi.createAccount;

    return this.http.send<ApiCreateAccount, ITcResponse<ICreateAccountResponse>>(new ApiCreateAccount(account)).pipe(
      tap(() => {
        this.isCreating$.next(false);
        this.hasCreateError$.next(false);
      }),
      map(() => true),
      catchError(() => {
        this.hasCreateError$.next(true);
        return of(false);
      })
    );
  }

  getGlobalNotifications(): Observable<INotification[]> {
    return this.http.send<ApiGetGlobalNotifications, ITcResponse<INotification[]>>(new ApiGetGlobalNotifications()).pipe(
      map((response: ITCResultResponse<INotification[]>) => response.result),
      catchError(() => of(null))
    );
  }
}
